import * as Sentry from '@sentry/react'

import { BOT_URL } from './config.js'

const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()

/** URLs that receive sentry-trace + baggage on outbound fetch/RPC. */
export function apiTracePropagationTargets(): (string | RegExp)[] {
  return [BOT_URL, /^https?:\/\/127\.0\.0\.1:8787/, /^https?:\/\/localhost:8787/, /backend-worker/]
}

function scrubSentryRequestAuthHeaders<
  T extends { request?: { headers?: Record<string, string> } },
>(event: T): T {
  if (event.request?.headers) {
    const headers = { ...event.request.headers }
    delete headers.Authorization
    delete headers.authorization
    event.request.headers = headers
  }
  return event
}

if (dsn) {
  // Testing phase: session replay at 1.0 sampling — reduce/disable before HIPAA go-live.
  Sentry.init({
    dsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ?? (import.meta.env.PROD ? 'production' : 'local'),
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: apiTracePropagationTargets(),
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    beforeSend(event) {
      return scrubSentryRequestAuthHeaders(event)
    },
  })
}

/** Propagate the active browser trace to backend-worker via Hono RPC headers. */
export function getOutboundTraceHeaders(): Record<string, string> {
  if (!dsn || !Sentry.isEnabled()) return {}

  const trace = Sentry.getTraceData()
  const headers: Record<string, string> = {}
  if (trace['sentry-trace']) headers['sentry-trace'] = trace['sentry-trace']
  if (trace.baggage) headers.baggage = trace.baggage
  return headers
}

export { Sentry }
